// Horizontal ranked bars: one row per item with a colored dot, label, value and
// a proportional track fill, scaled to the largest value.
import { Box, Flex, Text, useColorModeValue } from "@chakra-ui/react";

/**
 * Renders a single ranked row: a labelled header line over a proportional bar.
 * @param {Object} props - label, value, color, pct (fill width 0-100),
 *   hint (optional secondary text shown before the value)
 * @returns {React.ReactElement}
 */
function RankRow({ label, value, color, pct, hint }) {
  const labelColor = useColorModeValue("secondaryGray.600", "secondaryGray.400");
  const valueColor = useColorModeValue("secondaryGray.900", "white");
  const track = useColorModeValue("secondaryGray.200", "whiteAlpha.200");
  return (
    <Box>
      <Flex justify="space-between" align="center" mb="6px">
        <Flex align="center" gap="8px" minW="0">
          <Box w="9px" h="9px" borderRadius="full" bg={color} flexShrink="0" />
          <Text fontSize="sm" color={labelColor} noOfLines={1}>{label}</Text>
        </Flex>
        <Flex align="baseline" gap="6px" flexShrink="0">
          {hint && <Text fontSize="10px" color={labelColor}>{hint}</Text>}
          <Text fontSize="sm" fontWeight="700" color={valueColor}>{value}</Text>
        </Flex>
      </Flex>
      <Box h="8px" borderRadius="full" bg={track} overflow="hidden">
        <Box h="100%" w={`${pct}%`} bg={color} borderRadius="full" transition="width .4s ease" />
      </Box>
    </Box>
  );
}

/**
 * Renders items as horizontal ranked bars, vertically centered to fill height.
 * @param {Object} props - items ([{label, value, color, hint?}]) with resolved colors
 * @returns {React.ReactElement}
 */
export default function RankBars({ items }) {
  const max = Math.max(1, ...items.map((it) => it.value));
  return (
    <Flex direction="column" flex="1" justify="center" gap="16px" w="100%" py="10px">
      {items.map((it, i) => (
        <RankRow key={i} {...it} pct={(it.value / max) * 100} />
      ))}
    </Flex>
  );
}
